import { useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { Chip, Stack, Typography, useMediaQuery } from "@mui/material";
import { Box } from "@mui/system";
import { GridActionsCellItem } from "@mui/x-data-grid";
import TilleyDataGrid from "components/shared/DataGrid/TilleyDataGrid";
import { CustomWidthTooltip } from "mui-toolkit/CustomWidthTooltip";
import DeleteConfirmationButton from "mui-toolkit/DeleteConfirmationButton";
import { formatNumber, formatPracticeName } from "utility/formatters";

import { NavigateIcon } from "../../../Icons/Icons";
import { CROP_YEAR_POTENTIAL } from "../helpers";

const getNameSummary = (value, row) => {
  return row?.business_name || row?.last_name;
};
const getRowHeightAuto = () => "auto";

export default function ProducerFarmCropDataTable({
  producerFarmCrops,
  isLoading,
  onChange,
  pageSizes = [5, 10, 25, 50, 100],
  rowHeight = 80,
}) {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"), { noSsr: true });
  const { crop_year } = useParams();

  const columns = useMemo(
    () => [
      {
        field: "farm_name",
        headerName: "Name",
        flex: 1,
        renderCell: (params) => (
          <Typography color="primary" component={Link} to={`./${params.row.id}`}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: "fsa_farm_number",
        headerName: "FSN",
        flex: 1,
      },
      {
        field: "reported_acreage",
        headerName: "Acres",
        flex: 1,
        valueFormatter: (value) => (value ? formatNumber(value, 2) : ""),
      },
      {
        field: "base_acreage",
        headerName: "Base Acres",
        flex: 1,
        valueFormatter: (value) => (value ? formatNumber(value, 2) : ""),
      },
      {
        field: "CropProfiles_Sort",
        headerName: "Crop Profiles",
        flex: 2,
        display: "flex",
        renderCell: (params) => {
          const currentProducerFarmCrop = params.row;
          return (
            <Box
              className="crop-profile-data-grid"
              sx={{
                width: "100%",
              }}
            >
              <Stack direction="row" spacing={0} sx={{ flexWrap: "wrap", rowGap: 0, columnGap: 1 }}>
                {currentProducerFarmCrop?.CropProfiles?.map((currentCropProfile, index) => (
                  <Chip
                    key={index}
                    label={`${currentCropProfile?.commodity_name} ${formatPracticeName(currentCropProfile?.practice_name)}`}
                    sx={{
                      textOverflow: "ellipsis",
                      textTransform: "uppercase",
                      background: "transparent",
                      color: "text.secondary",
                      fontWeight: 600,
                      fontSize: "14px",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          );
        },
      },
      {
        field: "last_name",
        headerName: "Producer",
        flex: 1,
        valueGetter: getNameSummary,
      },
      {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        headerAlign: "right",
        align: "right",
        width: 100,
        sortable: false,
        getActions: (params) => {
          const title = params.row.farm_name ? params.row.farm_name : params.row.fsa_farm_number;
          return [
            crop_year === CROP_YEAR_POTENTIAL && (
              <CustomWidthTooltip key="delete" title="Delete" placement="top" arrow>
                <DeleteConfirmationButton item={params.row} onChange={onChange} />
              </CustomWidthTooltip>
            ),
            <GridActionsCellItem
              key="view"
              icon={<NavigateIcon />}
              label="View Details"
              data-testid={`Open ${title} - #${params.row.fsa_farm_number}`}
              onClick={() => navigate(`./${params.row.id}`)}
            />,
          ].filter(Boolean);
        },
        flex: 0,
      },
    ],
    [navigate, getNameSummary, formatPracticeName, formatNumber]
  );

  return (
    <>
      <TilleyDataGrid
        rows={producerFarmCrops || []}
        columns={columns}
        pageSizeOptions={pageSizes}
        defaultPageSize={isMobile ? 5 : 10}
        gridKey="producer-farm-crops"
        getRowId={(row) => row.id}
        getRowHeight={getRowHeightAuto}
        isLoading={isLoading}
        sx={{
          width: "99%",
          boxSizing: "border-box",
          "& .MuiDataGrid-row": {
            minHeight: rowHeight,
          },
          "& .MuiDataGrid-cell": {
            minHeight: rowHeight,
            display: "flex",
            alignItems: "center",
          },
        }}
      />
    </>
  );
}
